import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Typography
} from '@mui/material';

const EditPromoCodeDialog = ({ open, onClose, promoCode, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  
  // Type-specific fields (read-only, just for display)
  const [percentDiscount, setPercentDiscount] = useState('');
  const [fixedPricePerUnit, setFixedPricePerUnit] = useState('');
  const [buyQuantity, setBuyQuantity] = useState('');
  const [getQuantity, setGetQuantity] = useState('');
  const [getDiscountPercent, setGetDiscountPercent] = useState('');
  
  // Usage limits
  const [usageLimitPerCustomer, setUsageLimitPerCustomer] = useState('');
  const [usageLimitTotal, setUsageLimitTotal] = useState('');
  
  // Minimum requirements
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState('');
  const [minimumOrderValue, setMinimumOrderValue] = useState('');
  
  // Validity period
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  
  // Internal notes
  const [internalNotes, setInternalNotes] = useState('');
  
  useEffect(() => {
    if (promoCode) {
      setDescription(promoCode.description || '');
      setStatus(promoCode.status || 'ACTIVE');
      setPercentDiscount(promoCode.percentDiscount ? String(promoCode.percentDiscount) : '');
      setFixedPricePerUnit(promoCode.fixedPricePerUnit ? String(promoCode.fixedPricePerUnit) : '');
      setBuyQuantity(promoCode.buyQuantity ? String(promoCode.buyQuantity) : '');
      setGetQuantity(promoCode.getQuantity ? String(promoCode.getQuantity) : '');
      setGetDiscountPercent(promoCode.getDiscountPercent ? String(promoCode.getDiscountPercent) : '');
      setUsageLimitPerCustomer(promoCode.usageLimitPerCustomer ? String(promoCode.usageLimitPerCustomer) : '');
      setUsageLimitTotal(promoCode.usageLimitTotal ? String(promoCode.usageLimitTotal) : '');
      setMinimumOrderQuantity(promoCode.minimumOrderQuantity ? String(promoCode.minimumOrderQuantity) : '');
      setMinimumOrderValue(promoCode.minimumOrderValue ? String(promoCode.minimumOrderValue) : '');
      setValidFrom(promoCode.validFrom ? new Date(promoCode.validFrom).toISOString().slice(0, 16) : '');
      setValidUntil(promoCode.validUntil ? new Date(promoCode.validUntil).toISOString().slice(0, 16) : '');
      setInternalNotes(promoCode.internalNotes || '');
    }
  }, [promoCode]);
  
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build data object (only include changed fields)
      const data = {
        description: description || undefined,
        status,
        usageLimitPerCustomer: usageLimitPerCustomer ? parseInt(usageLimitPerCustomer) : undefined,
        usageLimitTotal: usageLimitTotal ? parseInt(usageLimitTotal) : undefined,
        minimumOrderQuantity: minimumOrderQuantity ? parseInt(minimumOrderQuantity) : undefined,
        minimumOrderValue: minimumOrderValue ? parseFloat(minimumOrderValue) : undefined,
        // Ensure validFrom is in ISO format if present
        validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
        // Ensure validUntil is in ISO format if present
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        internalNotes: internalNotes || undefined
      };
      
      await onUpdate(promoCode.id, data);
      
      onClose();
    } catch (err) {
      console.error('Error updating promo code:', err);
      setError(err.message || 'Failed to update promo code');
    } finally {
      setLoading(false);
    }
  };
  
  if (!promoCode) return null;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Promo Code: {promoCode.code}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Alert severity="info" sx={{ mb: 2 }}>
          Code and promo type cannot be changed after creation.
        </Alert>
        
        {/* Read-only fields */}
        <TextField
          fullWidth
          label="Promo Code"
          value={promoCode.code}
          disabled
          sx={{ mb: 2, mt: 1 }}
        />
        
        <TextField
          fullWidth
          label="Organization"
          value={promoCode.organization}
          disabled
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          label="Promo Type"
          value={promoCode.promoType}
          disabled
          sx={{ mb: 2 }}
        />
        
        {/* Editable fields */}
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Internal description for this promo"
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
          </Select>
        </FormControl>
        
        {/* Display current discount settings (read-only) */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Current Discount Settings</Typography>
        </Divider>
        
        {promoCode.promoType === 'PERCENTAGE_DISCOUNT' && (
          <TextField
            fullWidth
            label="Discount Percentage"
            value={percentDiscount}
            disabled
            InputProps={{ 
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
        )}
        
        {promoCode.promoType === 'FIXED_PRICE' && (
          <TextField
            fullWidth
            label="Fixed Price Per Unit"
            value={fixedPricePerUnit}
            disabled
            InputProps={{ 
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
        )}
        
        {(promoCode.promoType === 'BUY_X_GET_Y_FREE' || promoCode.promoType === 'BUY_X_GET_Y_DISCOUNTED') && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={promoCode.promoType === 'BUY_X_GET_Y_DISCOUNTED' ? 4 : 6}>
              <TextField
                fullWidth
                label="Buy Quantity"
                value={buyQuantity}
                disabled
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={promoCode.promoType === 'BUY_X_GET_Y_DISCOUNTED' ? 4 : 6}>
              <TextField
                fullWidth
                label="Get Quantity"
                value={getQuantity}
                disabled
                sx={{ mb: 2 }}
              />
            </Grid>
            {promoCode.promoType === 'BUY_X_GET_Y_DISCOUNTED' && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Get Discount %"
                  value={getDiscountPercent}
                  disabled
                  InputProps={{ 
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}
          </Grid>
        )}
        
        {/* Usage Limits (editable) */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Usage Limits</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Usage Limit Per Customer"
              value={usageLimitPerCustomer}
              onChange={(e) => setUsageLimitPerCustomer(e.target.value)}
              helperText="Leave empty for unlimited"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Total Usage Limit"
              value={usageLimitTotal}
              onChange={(e) => setUsageLimitTotal(e.target.value)}
              helperText="Total times code can be used"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          Current usage count: {promoCode.currentUsageCount || 0}
        </Alert>
        
        {/* Minimum Requirements (editable) */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Minimum Requirements</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Order Quantity"
              value={minimumOrderQuantity}
              onChange={(e) => setMinimumOrderQuantity(e.target.value)}
              helperText="Minimum units required"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Order Value"
              value={minimumOrderValue}
              onChange={(e) => setMinimumOrderValue(e.target.value)}
              InputProps={{ 
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText="Minimum dollar amount"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        {/* Validity Period (editable) */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Validity Period</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Valid From"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for immediate"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Valid Until"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to never expire"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        {/* Internal Notes (editable) */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Internal Notes"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          helperText="Private notes for admin use only"
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Updating...' : 'Update Promo Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPromoCodeDialog;

